/**
 * 移动端生命周期监听（§7.10）—— 这是「联机在手机上到底能不能用」的分水岭。
 *
 * iOS Safari 的真实行为（每一条都踩过）：
 *  1. 切后台后 timer 被节流到近乎停止，心跳自然也停了；
 *  2. 约 30 秒~数分钟后系统回收 socket，但 `onclose`/`onerror` **可能不触发**，
 *     或者拖到回前台那一刻才补触发；
 *  3. 这期间 `ws.readyState` **仍可能读到 1 (OPEN)**。
 *
 * 所以回前台时**必须主动探活**（发 ping + 设超时），不能假设连接还活着；
 * 后台待得太久（> `WS.BG_HARD_RECONNECT_MS`）干脆直接硬重连 —— 探活要等 3 秒才知道死了，
 * 而这种情况下它 99% 已经死了，直接重建更快也更省电。
 *
 * 另外三条容易漏：
 *  - `pagehide` 要主动 `close(1001)`，否则服务端要等心跳超时（45s）才回收，
 *    这段时间座位一直算「在线」，**断线宽限期根本不会开始计时**；
 *  - `pageshow.persisted`（bfcache 恢复）必须硬重连：页面是从缓存里捞回来的，
 *    里面那条 socket 早就没了，但 JS 状态看起来完好无损；
 *  - `offline` 要停掉自动重连：没网的时候重连只是白烧电池，`online` 回来再重置退避。
 */
import { WS } from '@poker/shared'

import type { BackoffResetReason } from './backoff.js'

/** 触发一次「解绑旧 socket → 重建」的原因，仅用于日志与埋点 */
export type ReconnectReason =
  | 'bgLong'
  | 'probeTimeout'
  | 'online'
  | 'bfcache'
  | 'pageResume'
  | 'manual'
  | 'dead'
  | 'closed'
  | 'authRefreshed'

/**
 * Transport 需要向生命周期层暴露的能力。
 * 刻意收得很窄：这一层不许碰 socket，只发号施令。
 */
export interface LifecycleHost {
  /** 记下进入后台的时刻（用注入的时钟，保证与 hiddenMs 同源） */
  markBackgrounded(atMs: number): void
  /** 进入后台的时刻；从未进过后台返回 null */
  backgroundedAt(): number | null
  resetBackoff(reason: BackoffResetReason): void
  /** ① 解绑旧 socket 的**全部**事件处理器 ② close 并置 null ③ 必要时刷新 token ④ 新建 */
  hardReconnect(reason: ReconnectReason): void
  probe(opts: { timeoutMs: number; onTimeout: () => void }): void
  /** 系统 offline：相位切 degraded，关掉 socket，停止自动重连 */
  goOffline(): void
  /** 页面卸载：标记为「主动关闭」并 close(1001)，让服务端立刻开始宽限期计时 */
  prepareUnload(): void
}

interface ListenerTarget {
  addEventListener(type: string, listener: (event: unknown) => void): void
  removeEventListener(type: string, listener: (event: unknown) => void): void
}

export interface LifecycleEnv {
  /** 没有 DOM（node 测试 / SSR）时给 null，attach 变成空操作 */
  readonly doc: (ListenerTarget & { readonly visibilityState?: string }) | null
  readonly win: ListenerTarget | null
  now(): number
}

export function browserLifecycleEnv(): LifecycleEnv {
  const g = globalThis as unknown as {
    document?: ListenerTarget & { visibilityState?: string }
    window?: ListenerTarget
    performance?: { now(): number }
  }
  const perf = g.performance
  return {
    doc: g.document ?? null,
    win: g.window ?? null,
    // performance.now() 单调，不会被系统校时带偏（Date.now() 会）
    now: typeof perf?.now === 'function' ? () => perf.now() : () => Date.now(),
  }
}

/**
 * 回前台时该做什么（§7.10）。单独抽出来是为了能直接单测这条分界线。
 */
export function foregroundAction(
  hiddenMs: number,
  hardReconnectAfterMs: number = WS.BG_HARD_RECONNECT_MS,
): 'hardReconnect' | 'probe' {
  return hiddenMs > hardReconnectAfterMs ? 'hardReconnect' : 'probe'
}

export interface LifecycleOptions {
  readonly env?: LifecycleEnv
  readonly hardReconnectAfterMs?: number
  readonly probeTimeoutMs?: number
}

/**
 * 挂上全部监听，返回卸载函数。**必须**在 Transport.close() 里调用返回值，
 * 否则换连接时会留下一批指向旧 socket 的监听，回前台一次触发好几条重连。
 */
export function attachLifecycle(host: LifecycleHost, options: LifecycleOptions = {}): () => void {
  const env = options.env ?? browserLifecycleEnv()
  const hardAfter = options.hardReconnectAfterMs ?? WS.BG_HARD_RECONNECT_MS
  const probeTimeout = options.probeTimeoutMs ?? WS.FG_PROBE_TIMEOUT_MS
  const { doc, win } = env
  const disposers: (() => void)[] = []

  const on = (target: ListenerTarget | null, type: string, fn: (event: unknown) => void): void => {
    if (target === null) return
    target.addEventListener(type, fn)
    disposers.push(() => {
      target.removeEventListener(type, fn)
    })
  }

  const onVisibility = (): void => {
    if (doc?.visibilityState !== 'visible') {
      host.markBackgrounded(env.now())
      return
    }
    // 用户正看着屏幕，这是最不能让他等的时刻
    host.resetBackoff('visible')
    const since = host.backgroundedAt()
    const hiddenMs = since === null ? 0 : env.now() - since
    if (foregroundAction(hiddenMs, hardAfter) === 'hardReconnect') {
      host.hardReconnect('bgLong')
      return
    }
    // ★ 不能假设连接还活着：readyState 会骗人，只有「发出去有没有回音」算数
    host.probe({
      timeoutMs: probeTimeout,
      onTimeout: () => {
        host.hardReconnect('probeTimeout')
      },
    })
  }

  on(doc, 'visibilitychange', onVisibility)

  on(win, 'pageshow', (event) => {
    const persisted = (event as { persisted?: boolean } | null)?.persisted === true
    if (!persisted) return
    // bfcache 里捞回来的页面：JS 状态完整，但那条 socket 早没了
    host.resetBackoff('pageshow')
    host.hardReconnect('bfcache')
  })

  on(win, 'pagehide', () => {
    host.prepareUnload()
  })

  on(win, 'offline', () => {
    host.goOffline()
  })

  on(win, 'online', () => {
    host.resetBackoff('online')
    host.hardReconnect('online')
  })

  // iOS 16.4+ / Chrome：从 freeze 状态恢复。与 visibilitychange 可能重复触发，
  // hardReconnect 自身必须幂等（内部会先解绑旧 socket）
  on(doc, 'resume', () => {
    host.hardReconnect('pageResume')
  })

  return () => {
    for (const d of disposers) d()
    disposers.length = 0
  }
}
